import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ArrowRight, Phone, MessageSquare } from "lucide-react";
import { SEOHead, faqSchema, breadcrumbSchema } from "../components/SEOHead";
import Breadcrumbs from "../components/Breadcrumbs";
import { gtmClick } from "@/lib/gtm";

const FAQ_CATEGORIES = [
  {
    title: "Getting Started",
    faqs: [
      { q: "How much does a website cost?", a: "Our website packages start from just $499 for a professional 8-page website. Our Business package is $899 and includes up to 15 pages, a blog, and advanced SEO. Our eCommerce package starts at $1,499 with a full online store. All prices are one-time — you only pay hosting ($99/year) and domain ($10/year) to keep it running. We also offer monthly payment plans over 12 months." },
      { q: "How long does it take to build my website?", a: "Most websites are live within 48 hours of receiving your content. More complex sites with custom features or eCommerce functionality may take 3–5 business days. We'll give you a clear timeline before we start." },
      { q: "What do I need to provide?", a: "We'll need your business logo (if you have one), any images you'd like to use, your written content (or we can write it for you with our copywriting add-on), and your domain name. Don't worry if you don't have everything — we'll guide you through it." },
      { q: "Do I need to know how to code?", a: "Absolutely not. We handle everything technical — design, development, hosting, SSL, and SEO setup. You just tell us what you want and we build it. We can also train you on making basic content updates if you'd like." },
      { q: "Can I see examples of your work?", a: "Yes! Visit our portfolio page to see real websites we've built for Australian businesses across a range of industries including trades, retail, professional services, and eCommerce." },
    ]
  },
  {
    title: "Design & Features",
    faqs: [
      { q: "Will my website be mobile-friendly?", a: "Every single website we build is fully responsive and mobile-optimised. With over 60% of web traffic coming from mobile devices in Australia, this is non-negotiable. Your site will look perfect on phones, tablets, and desktops." },
      { q: "Can I have a blog on my website?", a: "Yes — blogs are included in our Business and eCommerce packages. A regularly updated blog is one of the best ways to improve your Google rankings. We'll set it up so you can easily add new posts." },
      { q: "Do you build online stores?", a: "Yes, our eCommerce package ($1,499) includes a full online store with up to 50 products, Stripe and PayPal payments, inventory management, customer accounts, and abandoned cart recovery." },
      { q: "Can I add a booking system?", a: "Absolutely. Our Business package includes booking system integration. We can connect tools like Calendly, Square Appointments, or other booking platforms to your site." },
      { q: "Will my website have an SSL certificate?", a: "Yes, every website we build includes a free SSL certificate (the padlock icon in the browser). This encrypts data, protects your visitors, and is essential for Google rankings." },
    ]
  },
  {
    title: "SEO & Marketing",
    faqs: [
      { q: "Will my website show up on Google?", a: "All our packages include foundational SEO setup — proper title tags, meta descriptions, heading structure, image optimisation, Google Analytics, and Google Search Console integration. We also submit your sitemap to Google. For more advanced SEO, we offer an SEO Setup & Analysis add-on ($299) that includes keyword research and on-page optimisation." },
      { q: "What is SEO and why does it matter?", a: "SEO (Search Engine Optimisation) is the practice of optimising your website so it ranks higher in Google search results. Good SEO means more organic traffic — people finding your business for free through Google, without paying for ads. It's the most cost-effective long-term marketing strategy." },
      { q: "Do you set up Google Analytics?", a: "Yes, Google Analytics 4 (GA4) is included with every website. We also set up Google Tag Manager so you can easily add tracking for Facebook Pixel, Google Ads, and other marketing platforms." },
      { q: "Can you help with Google Ads or social media?", a: "While we specialise in web design, our Business and eCommerce packages are 'Google Ads ready' — meaning we set up conversion tracking and landing pages optimised for paid campaigns. We can recommend trusted partners for ongoing ad management." },
      { q: "How do I get more traffic to my website?", a: "The best approach combines several strategies: SEO-optimised content (blog posts targeting keywords your customers search for), Google My Business listing, social media presence, and potentially paid advertising. A well-built website is the foundation — everything else builds on it." },
    ]
  },
  {
    title: "Hosting & Domain",
    faqs: [
      { q: "Do I need hosting?", a: "Yes, web hosting is like renting space on the internet for your website. We offer Australian hosting for $99/year which includes SSL, daily backups, 99.9% uptime, and technical support. This is billed annually." },
      { q: "Can I use my own domain name?", a: "Absolutely. If you already own a domain, we'll connect it to your new website. If you need one, we can register a .com.au domain for just $10/year." },
      { q: "What happens if my website goes down?", a: "Our hosting includes 99.9% uptime guarantee. In the rare event of downtime, our monitoring systems detect it immediately and we work to resolve it. We also maintain daily backups so your data is always safe." },
      { q: "Can I move my website elsewhere later?", a: "Yes, you own your website. If you ever want to move to different hosting, we'll provide all your website files. We want to earn your business every year, not lock you in." },
    ]
  },
  {
    title: "Payments & Support",
    faqs: [
      { q: "What payment methods do you accept?", a: "We accept all major credit and debit cards (Visa, Mastercard, Amex) through our secure Stripe payment system. You can pay upfront or choose our 12-month payment plan." },
      { q: "Is there a money-back guarantee?", a: "Yes, we offer a 30-day money-back guarantee. If you're not happy with your website within 30 days of delivery, we'll issue a full refund. No questions asked." },
      { q: "Do you offer ongoing support?", a: "Our Starter package includes 30-day post-launch support. Business packages include 60 days, and eCommerce includes 90 days. After that, we're always available for updates and changes — just get in touch." },
      { q: "Can I make changes to my website after it's built?", a: "Yes! We build websites that are easy to update. You can make basic content changes yourself, or contact us for design changes and new features. During your support period, minor updates are included free." },
      { q: "Do you offer payment plans?", a: "Yes, we offer a 12-month payment plan on all packages. This lets you spread the cost into affordable monthly payments. For example, our Starter package works out to around $52/month." },
    ]
  },
  {
    title: "Common Website Problems",
    faqs: [
      { q: "My current website is slow — can you fix that?", a: "Slow websites are usually caused by unoptimised images, bloated code, poor hosting, or too many plugins. Our websites are built lean and fast from the ground up, hosted on Australian servers with CDN caching. Most of our sites score 90+ on Google PageSpeed Insights." },
      { q: "My website doesn't show up in Google search results", a: "This is usually caused by missing SEO fundamentals — no meta tags, no sitemap submitted, poor heading structure, or thin content. Our packages include proper SEO setup to fix these issues. If you have an existing site, we can audit it and build a replacement that's properly optimised." },
      { q: "My website isn't mobile-friendly", a: "Google prioritises mobile-friendly websites in search results. If your site isn't responsive, you're losing both rankings and customers. Every website we build is mobile-first — designed for phones first, then scaled up for desktop." },
      { q: "I built my own website but it looks unprofessional", a: "DIY website builders can only take you so far. A professionally designed website builds trust, looks credible, and converts visitors into customers. Our sites are custom-designed to match your brand — not a cookie-cutter template." },
      { q: "My website was hacked or has security issues", a: "Old, unmaintained websites are vulnerable to attacks. We build secure websites with SSL encryption, regular updates, and daily backups. Moving to a professionally hosted and maintained website eliminates most security risks." },
      { q: "I'm paying too much for my current website", a: "Many businesses pay $3,000–$10,000+ for websites that could be built for a fraction of the cost. Our packages start at $499 with no hidden fees. We believe great web design shouldn't cost a fortune." },
    ]
  },
];

const ALL_FAQS = FAQ_CATEGORIES.flatMap(c => c.faqs);

const crumbs = [{ name: "FAQ", path: "/faq" }];

export default function FAQPage() {
  const [openIdx, setOpenIdx] = useState(null);
  const [activeCategory, setActiveCategory] = useState(0);

  const toggle = (idx) => setOpenIdx(openIdx === idx ? null : idx);

  const schema = [
    faqSchema(ALL_FAQS),
    breadcrumbSchema([{ name: "Home", path: "/" }, ...crumbs])
  ];

  return (
    <main className="pt-16" data-testid="faq-page">
      <SEOHead
        title="Frequently Asked Questions"
        description="Find answers to common questions about our web design services, pricing, SEO, hosting, domains, and support. No Fuss Websites — professional websites from $499 in Melbourne."
        path="/faq"
        schema={schema}
      />
      <Breadcrumbs items={crumbs} />

      {/* Hero */}
      <section className="bg-[#FFD600] border-b-2 border-black py-16">
        <div className="container-main text-center">
          <h1 className="text-4xl md:text-6xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Frequently Asked Questions
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto">
            Everything you need to know about getting a professional website for your business.
          </p>
        </div>
      </section>

      {/* Category tabs */}
      <section className="border-b-2 border-black bg-white sticky top-16 z-30">
        <div className="container-main flex gap-1 overflow-x-auto py-2 scrollbar-hide">
          {FAQ_CATEGORIES.map((cat, i) => (
            <button
              key={i}
              onClick={() => { setActiveCategory(i); setOpenIdx(null); }}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold border-2 border-black transition-all ${activeCategory === i ? "bg-black text-[#FFD600]" : "bg-white hover:bg-gray-100"}`}
              data-testid={`faq-cat-${i}`}
            >
              {cat.title}
            </button>
          ))}
        </div>
      </section>

      {/* FAQ List */}
      <section className="section-padding bg-white">
        <div className="container-main max-w-3xl">
          <h2 className="text-2xl font-black mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>
            {FAQ_CATEGORIES[activeCategory].title}
          </h2>
          <div className="space-y-3">
            {FAQ_CATEGORIES[activeCategory].faqs.map((faq, i) => (
              <div
                key={i}
                className="border-2 border-black rounded-xl overflow-hidden"
                style={{ boxShadow: openIdx === i ? "3px 3px 0px 0px rgba(0,0,0,1)" : "none" }}
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => toggle(i)}
                  className="w-full flex items-center justify-between p-5 text-left font-bold hover:bg-gray-50 transition-colors"
                >
                  <span className="pr-4">{faq.q}</span>
                  <ChevronDown size={18} className={`shrink-0 transition-transform ${openIdx === i ? "rotate-180" : ""}`} />
                </button>
                {openIdx === i && (
                  <div className="px-5 pb-5 text-gray-700 leading-relaxed border-t border-gray-200 pt-4">
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Links / CTA */}
      <section className="section-padding bg-[#FDFDFD] border-t-2 border-black">
        <div className="container-main text-center">
          <h2 className="text-3xl font-black mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>
            Still Have Questions?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-xl mx-auto">
            We're here to help. Get in touch and we'll get back to you within 24 hours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary" onClick={() => gtmClick("faq_contact")}>
              <MessageSquare size={18} /> Contact Us
            </Link>
            <Link to="/quote" className="bg-white border-2 border-black font-bold px-6 py-3 rounded-full inline-flex items-center gap-2 hover:-translate-y-0.5 transition-all" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }} onClick={() => gtmClick("faq_quote")}>
              Get a Free Quote <ArrowRight size={18} />
            </Link>
            <a href="tel:0466476264" className="bg-white border-2 border-black font-bold px-6 py-3 rounded-full inline-flex items-center gap-2 hover:-translate-y-0.5 transition-all" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}>
              <Phone size={18} /> 0466 476 264
            </a>
          </div>
        </div>
      </section>

      {/* Related Pages */}
      <section className="section-padding bg-white border-t-2 border-black">
        <div className="container-main">
          <h2 className="text-2xl font-black mb-6" style={{ fontFamily: "Outfit, sans-serif" }}>Helpful Resources</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { title: "View Our Packages", desc: "Compare our Starter, Business, and eCommerce website packages.", link: "/packages", cta: "See Pricing" },
              { title: "How It Works", desc: "Our simple 4-step process from enquiry to launch.", link: "/how-it-works", cta: "Learn More" },
              { title: "Our Portfolio", desc: "See real websites we've built for Australian businesses.", link: "/portfolio", cta: "View Work" },
            ].map((item, i) => (
              <Link key={i} to={item.link} className="border-2 border-black rounded-xl p-6 hover:-translate-y-1 transition-all bg-white" style={{ boxShadow: "3px 3px 0px 0px rgba(0,0,0,1)" }}>
                <h3 className="font-black text-lg mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 mb-3">{item.desc}</p>
                <span className="text-sm font-bold flex items-center gap-1">{item.cta} <ArrowRight size={14} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
